import { useSidebar } from '@heidi/design-system/page-shell';
import { useMediaQuery } from '@heidi/utils';
import { useCallback, useEffect, useRef } from 'react';
import {
  useRecentVisitHoverCardController,
  type RecentVisitHoverTarget,
} from './recent-visit-hover-card-context';

interface UseRecentVisitHoverTargetArgs extends RecentVisitHoverTarget {
  /** The card is suppressed while the row's fragment cluster is open — it would
   *  cover the very thing the click just revealed. */
  isExpanded: boolean;
}

export interface RecentVisitHoverTargetBinding {
  /** The row's `<li>` — the focus host, and what the card anchors against. */
  rowRef: React.RefObject<HTMLLIElement>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * One row's half of the shared hover card: when to ask for it, and the
 * keyboard route into it.
 *
 * Pointer state leaves here as handlers because `NavItem` already exposes
 * mouse props. Focus and keyboard state are bound to the row element instead:
 * `focusin` / `focusout` / `keydown` are behaviours this one row needs, and
 * threading three more handler props through `NavItem` would put them on every
 * nav row in the app to serve one.
 *
 * Tab is what carries a keyboard user from the row into the card's toolbar.
 * The card owns the way back out (see `RecentVisitHoverCard`), so the pair
 * reads as a single tab stop that happens to contain three buttons.
 */
export function useRecentVisitHoverTarget({
  session,
  sessionLabel,
  patientName,
  isExpanded,
}: UseRecentVisitHoverTargetArgs): RecentVisitHoverTargetBinding {
  const card = useRecentVisitHoverCardController();
  const rowRef = useRef<HTMLLIElement>(null);

  const { state: sidebarState } = useSidebar();
  // A collapsed rail already shows `SidebarMenuButton`'s tooltip on hover, and
  // a touch device has no hover to speak of — in both cases the card is the
  // wrong affordance rather than a differently-timed one.
  const canHover = useMediaQuery('(hover: hover)', true);
  const isSuppressed =
    isExpanded ||
    Boolean(session.is_offline_synthetic) ||
    sidebarState === 'collapsed' ||
    !canHover;

  const isShowingThisRow = card.activeSessionId === session.session_id;

  // Kept in a ref so the DOM listeners below can read the current target
  // without rebinding on every label or patient change.
  const targetRef = useRef<RecentVisitHoverTarget>({
    session,
    sessionLabel,
    patientName,
  });
  targetRef.current = { session, sessionLabel, patientName };

  const { requestOpen, requestClose, close, focusFirstAction, contentRef } = card;

  const onMouseEnter = useCallback(() => {
    // A suppressed row still has to take the card DOWN as the pointer reaches
    // it, or the card sits there describing the row above. Scheduled rather
    // than immediate, so passing over one offline row on the way down the list
    // is a pause and not a flicker.
    if (isSuppressed) return requestClose();
    const row = rowRef.current;
    if (row) requestOpen(targetRef.current, row);
  }, [isSuppressed, requestClose, requestOpen]);

  // Suppression can arrive while the card is up — expanding the row is a click
  // made with the pointer sitting on it, which delivers no leave event.
  useEffect(() => {
    if (isSuppressed && isShowingThisRow) close();
  }, [isSuppressed, isShowingThisRow, close]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || isSuppressed) return;

    const handleFocusIn = () => requestOpen(targetRef.current, row, { immediate: true });

    const handleFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node)) return close();
      // Tabbing into the card is not leaving the row as far as the card is
      // concerned — the portal puts the toolbar outside the `<li>` in the DOM.
      if (row.contains(next) || contentRef.current?.contains(next)) return;
      close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') return close();
      if (event.key !== 'Tab' || event.shiftKey) return;
      if (focusFirstAction()) event.preventDefault();
    };

    row.addEventListener('focusin', handleFocusIn);
    row.addEventListener('focusout', handleFocusOut);
    row.addEventListener('keydown', handleKeyDown);
    return () => {
      row.removeEventListener('focusin', handleFocusIn);
      row.removeEventListener('focusout', handleFocusOut);
      row.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSuppressed, requestOpen, close, focusFirstAction, contentRef]);

  return { rowRef, onMouseEnter, onMouseLeave: requestClose };
}
